import SeoHead from "../components/SeoHead";
import { localeRoutes } from "../i18n/siteContent";

const contentByLocale = {
    en: {
        title: "Fractional CTO | Engineering Delivery Transformation Case Study",
        description:
            "Discursive NDA-safe case study on transforming engineering delivery into a scalable system through ownership, CI discipline, architectural modernization, and AWS cost awareness.",
        eyebrow: "Case Study",
        heading: "Transforming Engineering Delivery into a Scalable System",
        opening: [
            "Three years ago, the engineering environment I stepped into was functional — but structurally fragile.",
            "The organization was growing, and project complexity was increasing. However, delivery practices had not evolved at the same pace.",
            "Releases depended on manual coordination. Environments were inconsistent. Continuous Integration was not part of the workflow. Architectural standards varied across projects.",
            "The real constraint was not talent. It was structure.",
            "To enable sustainable growth, engineering needed to shift from effort-based delivery to system-based execution."
        ],
        beforeTitle: "INITIAL STATE (BEFORE)",
        beforeLead: "At the outset:",
        before: [
            "Deployments required manual steps and cross-team coordination.",
            "There was no consistent CI discipline or automated quality gates.",
            "Development environments differed across machines and projects.",
            "Architectural decisions were not standardized.",
            "Project complexity was effectively capped by operational fragility.",
            "Each release introduced avoidable risk."
        ],
        transformTitle: "STRUCTURAL TRANSFORMATION",
        transformLead:
            "The transformation focused on building a predictable, reproducible delivery system.",
        transformBlocks: [
            {
                title: "A) Environment Standardization",
                items: [
                    "Docker was introduced as a baseline for major projects, creating reproducible environments across the team.",
                    "This reduced environment-related friction and improved onboarding consistency."
                ]
            },
            {
                title: "B) Continuous Integration Discipline",
                items: [
                    "Automated CI pipelines were introduced to validate changes before deployment.",
                    "Unit tests and coding standards became enforced quality gates rather than optional practices.",
                    "This reduced regression risk and increased confidence in releases."
                ]
            },
            {
                title: "C) Operating Model Shift",
                items: [
                    "A pragmatic Agile structure was introduced, centered on clear domain ownership and defined responsibilities.",
                    "Agile was treated as an operating system for predictable delivery, not a ceremony."
                ]
            }
        ],
        evolutionTitle: "ARCHITECTURAL EVOLUTION",
        evolutionIntro:
            "As structural maturity increased, architectural ambition could grow.",
        evolution: [
            "Headless patterns were introduced on critical delivery paths.",
            "React-based frontends were implemented where scalability and decoupling were required.",
            "Frontend and backend concerns became clearly separated, enabling more flexible evolution of the platform."
        ],
        impactTitle: "BUSINESS IMPACT",
        impactIntro:
            "The impact of this transformation was structural rather than incremental.",
        impact: [
            "Deployment cycles moved from manual, multi-step coordination to automated pipeline-based releases.",
            "Production gaps linked to environment inconsistencies decreased significantly.",
            "Issue resolution became faster due to reproducible environments and enforced quality checks.",
            "Engineering maturity increased across teams.",
            "Most importantly, the organization became capable of delivering larger and more complex applications and portals than before."
        ],
        impactClosing:
            "The transformation did not simply improve process efficiency. It expanded the company’s commercial and technical capacity.",
        credibilityTitle: "Credibility Signals (NDA-safe)",
        credibility: [
            "Three-year transformation executed progressively across high-complexity projects",
            "Delivery standardization adopted as the default for major engagements",
            "CI discipline enforced before production deployments",
            "Headless/React architectures introduced on critical paths",
            "Ownership-based operating model embedded in the team structure"
        ],
        closingTitle: "CLOSING INSIGHT",
        closing: [
            "Sustainable growth is not unlocked by heroic effort.",
            "It is unlocked by building structured systems that reduce friction, clarify ownership, and make delivery predictable."
        ]
    },
    it: {
        title: "Fractional CTO | Case Study Trasformazione Delivery Engineering",
        description:
            "Case study NDA-safe con narrativa discorsiva sulla trasformazione della delivery engineering in un sistema scalabile con ownership, disciplina CI, modernizzazione architetturale e attenzione ai costi AWS.",
        eyebrow: "Case Study",
        heading: "Trasformare la delivery engineering in un sistema scalabile",
        opening: [
            "Tre anni fa, l’ambiente engineering in cui sono entrato era operativo — ma strutturalmente fragile.",
            "L’azienda stava crescendo e la complessità dei progetti aumentava. Tuttavia, le pratiche di delivery non si erano evolute con la stessa velocità.",
            "I rilasci dipendevano da coordinamenti manuali. Gli ambienti non erano standardizzati. La Continuous Integration non faceva parte del flusso di lavoro. Gli standard architetturali variavano tra progetti.",
            "Il limite non era il talento del team. Era la struttura.",
            "Per rendere la crescita sostenibile, era necessario passare da una delivery basata sull’impegno individuale a un sistema di esecuzione strutturato."
        ],
        beforeTitle: "INITIAL STATE (BEFORE)",
        beforeLead: "All’inizio:",
        before: [
            "I deploy richiedevano passaggi manuali e coordinamento tra persone.",
            "Non esisteva una disciplina CI con controlli automatici.",
            "Gli ambienti di sviluppo erano eterogenei.",
            "Le scelte architetturali non erano uniformi.",
            "La complessità dei progetti era di fatto limitata dalla fragilità operativa.",
            "Ogni rilascio introduceva rischi evitabili."
        ],
        transformTitle: "STRUCTURAL TRANSFORMATION",
        transformLead:
            "L’intervento si è concentrato sulla costruzione di un sistema di delivery prevedibile e riproducibile.",
        transformBlocks: [
            {
                title: "A) Standardizzazione degli ambienti",
                items: [
                    "Docker è stato introdotto come baseline per i progetti di maggiore dimensione, rendendo gli ambienti coerenti e replicabili.",
                    "Questo ha ridotto l’attrito operativo e semplificato l’onboarding."
                ]
            },
            {
                title: "B) Disciplina di Continuous Integration",
                items: [
                    "Sono state introdotte pipeline CI automatiche per validare ogni modifica prima del rilascio.",
                    "Unit test e standard di codice sono diventati controlli obbligatori, non opzionali.",
                    "Il risultato è stato una riduzione del rischio di regressioni e una maggiore affidabilità dei rilasci."
                ]
            },
            {
                title: "C) Evoluzione del modello operativo",
                items: [
                    "È stato introdotto un modello Agile pragmatico, basato su ownership chiara e responsabilità definite.",
                    "Agile non come rituale, ma come sistema operativo per una delivery prevedibile."
                ]
            }
        ],
        evolutionTitle: "ARCHITECTURAL EVOLUTION",
        evolutionIntro:
            "Con l’aumentare della maturità strutturale, è stato possibile alzare l’ambizione architetturale.",
        evolution: [
            "Sono stati introdotti pattern headless sui flussi critici.",
            "React è stato adottato dove era necessario garantire scalabilità e separazione delle responsabilità.",
            "Frontend e backend sono stati decoupled, consentendo un’evoluzione più flessibile della piattaforma."
        ],
        impactTitle: "BUSINESS IMPACT",
        impactIntro:
            "L’impatto è stato strutturale, non incrementale.",
        impact: [
            "I cicli di deploy sono passati da coordinamenti manuali a rilasci automatizzati tramite pipeline.",
            "Le criticità in produzione legate a inconsistenze ambientali si sono ridotte.",
            "La risoluzione dei problemi è diventata più rapida grazie ad ambienti riproducibili e controlli qualità automatici.",
            "Il livello di maturità engineering è cresciuto in modo significativo.",
            "L’azienda ha acquisito la capacità di gestire applicazioni e portali di complessità molto superiore rispetto al passato."
        ],
        impactClosing:
            "La trasformazione non ha solo migliorato l’efficienza. Ha ampliato la capacità commerciale e tecnica dell’organizzazione.",
        credibilityTitle: "Indicatori di credibilità (NDA-safe)",
        credibility: [
            "Trasformazione strutturale sviluppata progressivamente nell’arco di tre anni",
            "Standard di delivery adottati come baseline nei progetti di maggiore complessità",
            "Disciplina CI obbligatoria prima dei rilasci in produzione",
            "Introduzione di architetture headless e React sui percorsi critici",
            "Modello operativo basato su ownership integrato nel team"
        ],
        closingTitle: "CLOSING INSIGHT",
        closing: [
            "La crescita sostenibile non nasce dallo sforzo eroico.",
            "Nasce dalla costruzione di sistemi strutturati che riducono l’attrito, chiariscono le responsabilità e rendono la delivery prevedibile."
        ]
    }
};

function EngineeringDeliveryCaseStudyPage({ locale = "en" }) {
    const text = contentByLocale[locale] || contentByLocale.en;
    const routes = localeRoutes[locale];
    const breadcrumbItems = [
        { name: "Home", path: routes.home },
        { name: locale === "it" ? "Casi studio" : "Case Studies", path: routes.caseStudies },
        { name: text.heading, path: routes.engineeringCaseStudy }
    ];

    return (
        <>
            <SeoHead
                title={text.title}
                description={text.description}
                keywords="Maurizio Brioschi case study, engineering delivery transformation, fractional cto, CI/CD, agile operating model, solution architecture, AWS architecture"
                path={routes.engineeringCaseStudy}
                locale={locale}
                enPath={localeRoutes.en.engineeringCaseStudy}
                itPath={localeRoutes.it.engineeringCaseStudy}
                breadcrumbItems={breadcrumbItems}
            />
            <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
                <header>
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">{text.eyebrow}</p>
                    <h1 className="mt-3 text-4xl font-impact text-slate-900">{text.heading}</h1>
                    <div className="mt-4 space-y-4 max-w-4xl text-slate-700">
                        {text.opening.map((paragraph) => (
                            <p key={paragraph}>{paragraph}</p>
                        ))}
                    </div>
                </header>

                <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
                    <h2 className="text-2xl font-impact text-slate-900">{text.beforeTitle}</h2>
                    <p className="mt-3 text-sm text-slate-700">{text.beforeLead}</p>
                    <ul className="mt-4 space-y-2 text-sm text-slate-700">
                        {text.before.map((item) => (
                            <li key={item}>- {item}</li>
                        ))}
                    </ul>
                </section>

                <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
                    <h2 className="text-2xl font-impact text-slate-900">{text.transformTitle}</h2>
                    <p className="mt-3 text-sm text-slate-700">{text.transformLead}</p>
                    <div className="mt-4 grid gap-4 md:grid-cols-3">
                        {text.transformBlocks.map((block) => (
                            <article key={block.title} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                <h3 className="text-base font-semibold text-slate-900">{block.title}</h3>
                                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                                    {block.items.map((item) => (
                                        <li key={item}>- {item}</li>
                                    ))}
                                </ul>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
                    <h2 className="text-2xl font-impact text-slate-900">{text.evolutionTitle}</h2>
                    <p className="mt-3 text-sm text-slate-700">{text.evolutionIntro}</p>
                    <ul className="mt-4 space-y-2 text-sm text-slate-700">
                        {text.evolution.map((item) => (
                            <li key={item}>- {item}</li>
                        ))}
                    </ul>
                </section>

                <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
                    <h2 className="text-2xl font-impact text-slate-900">{text.impactTitle}</h2>
                    <p className="mt-3 text-sm text-slate-700">{text.impactIntro}</p>
                    <ul className="mt-4 space-y-2 text-sm text-slate-700">
                        {text.impact.map((item) => (
                            <li key={item}>- {item}</li>
                        ))}
                    </ul>
                    <p className="mt-4 text-sm text-slate-700">{text.impactClosing}</p>
                </section>

                <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
                    <h2 className="text-2xl font-impact text-slate-900">{text.credibilityTitle}</h2>
                    <ul className="mt-4 space-y-2 text-sm text-slate-700">
                        {text.credibility.map((item) => (
                            <li key={item}>- {item}</li>
                        ))}
                    </ul>
                </section>

                <section className="mt-8 rounded-2xl border border-slate-200 bg-slate-900 p-6 text-white">
                    <h2 className="text-2xl font-impact">{text.closingTitle}</h2>
                    <div className="mt-3 space-y-3 text-sm text-slate-100">
                        {text.closing.map((paragraph) => (
                            <p key={paragraph}>{paragraph}</p>
                        ))}
                    </div>
                </section>
            </main>
        </>
    );
}

export default EngineeringDeliveryCaseStudyPage;
